import { Button, Group, Modal, Stack } from '@mantine/core';
import { zodResolver } from '@mantine/form';
import moment from 'moment';
import { z } from 'zod';

import { Document, EnvironmentCountries, EORIGIN_DATE_FORMAT } from '@e-origin/shared';

import { DatePickerInput, EucdmCode, MultiLangCodes, TextInput } from '@components';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { useFormWithNameInputProp } from '@hooks/useFormWithNameInputProp';
import { useSettings } from '@hooks/useSettings';

interface ManageSupportingDocumentViewProps {
  opened: boolean;
  onHide: () => void;
  docDetails?: Document;
  onSave: (savedDocument: Document, originalDocument: Document) => void;
  editDisabled: boolean;
}

const supportingDocumentSchema = z.object({
  code: z.string().min(1, { message: 'Code is required' }),
  referenceNumber: z.string(),
  dateOfValidity: z.date().optional(),
});

type SupportingDocumentValues = z.infer<typeof supportingDocumentSchema>;

export const ManageSupportingDocumentView: React.FC<ManageSupportingDocumentViewProps> = (props) => {
  const { opened, onHide, docDetails, onSave, editDisabled } = props;
  const { data: settings } = useSettings();
  const isNetherlands = settings.country === EnvironmentCountries.NL;
  const codelistSystems = useCodeListSystem({});

  const form = useFormWithNameInputProp<SupportingDocumentValues>({
    initialValues: {
      code: docDetails?.type || '',
      referenceNumber: docDetails?.referenceNumber || '',
      dateOfValidity: docDetails?.dateOfValidity ? moment(docDetails?.dateOfValidity, EORIGIN_DATE_FORMAT).toDate() : undefined,
    },
    validate: zodResolver(supportingDocumentSchema),
    validateInputOnBlur: true,
  });

  const onSubmit = (values: SupportingDocumentValues) => {
    onSave(
      {
        ...docDetails,
        type: values.code,
        referenceNumber: values.referenceNumber,
        dateOfValidity: values.dateOfValidity ? moment(values.dateOfValidity, EORIGIN_DATE_FORMAT).format(EORIGIN_DATE_FORMAT) : undefined,
      },
      docDetails,
    );
    onHide();
  };

  return (
    <Modal title="Edit additional reference document" opened={opened} onClose={onHide} zIndex={9999} centered size="lg" withOverlay={false}>
      <Stack gap="md" p="md">
        <EucdmCode path="documents.additionalReference.type">
          <MultiLangCodes
            label="Code"
            {...form.getInputProps('code')}
            code="CL380"
            system={codelistSystems}
            disabled={!!docDetails || editDisabled}
            size="lg"
          />
        </EucdmCode>

        <EucdmCode path="documents.additionalReference.referenceNumber">
          <TextInput label="Reference" {...form.getInputProps('referenceNumber')} disabled={editDisabled} size="lg" />
        </EucdmCode>

        {!isNetherlands && <DatePickerInput label="Date of validity" {...form.getInputProps('dateOfValidity')} size="lg" />}
      </Stack>

      <Group justify="flex-end" p="md">
        <Button type="button" onClick={onHide} size="lg" variant="transparent">
          Cancel
        </Button>
        <Button type="button" variant="filled" size="lg" onClick={() => onSubmit(form.values)} disabled={editDisabled}>
          Save
        </Button>
      </Group>
    </Modal>
  );
};
